import React from "react";
import {
    Chart as ChartJS,
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler,
} from "chart.js";
import { Line } from "react-chartjs-2";

ChartJS.register(
    CategoryScale,
    LinearScale,
    PointElement,
    LineElement,
    Title,
    Tooltip,
    Legend,
    Filler
);

/**
 * Reusable LineChart component.
 *
 * Props:
 *  - labels: string[]          — X-axis labels (e.g. months)
 *  - datasets: Array<{
 *      label: string,
 *      data: number[],
 *      borderColor?: string,
 *      backgroundColor?: string,
 *      fill?: boolean,
 *    }>
 *  - title?: string
 *  - height?: number           — px height of chart container (default 260)
 *  - yAxisLabel?: string
 */
const LineChart = ({ labels = [], datasets = [], title, height = 260, yAxisLabel }) => {
    const data = {
        labels,
        datasets: datasets.map((ds, i) => ({
            fill: ds.fill ?? false,
            tension: 0.4,
            pointRadius: 4,
            pointHoverRadius: 6,
            borderWidth: 2.5,
            ...ds,
        })),
    };

    const options = {
        responsive: true,
        maintainAspectRatio: false,
        interaction: { mode: "index", intersect: false },
        plugins: {
            legend: {
                position: "top",
                align: "end",
                labels: {
                    boxWidth: 10,
                    boxHeight: 10,
                    borderRadius: 3,
                    useBorderRadius: true,
                    padding: 16,
                    font: { size: 11, family: "'Inter', sans-serif" },
                    color: "#6B7280",
                },
            },
            tooltip: {
                backgroundColor: "#1F2937",
                titleFont: { size: 12, family: "'Inter', sans-serif" },
                bodyFont: { size: 11, family: "'Inter', sans-serif" },
                padding: 10,
                cornerRadius: 8,
            },
            title: { display: false },
        },
        scales: {
            x: {
                grid: { display: false },
                border: { display: false },
                ticks: {
                    font: { size: 11, family: "'Inter', sans-serif" },
                    color: "#9CA3AF",
                },
            },
            y: {
                grid: { color: "#F3F4F6" },
                border: { display: false, dash: [4, 4] },
                ticks: {
                    font: { size: 11, family: "'Inter', sans-serif" },
                    color: "#9CA3AF",
                    stepSize: 1,
                },
                title: yAxisLabel
                    ? { display: true, text: yAxisLabel, color: "#9CA3AF", font: { size: 10 } }
                    : { display: false },
            },
        },
    };

    return (
        <div style={{ height: `${height}px`, width: "100%", position: "relative" }}>
            <Line data={data} options={options} />
        </div>
    );
};

export default LineChart;
